
"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname } from "next/navigation";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";

import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "@/app/context/ThemeContext";

 

interface LogoData {
  namelogo: string;
  url: string | null;
  Reference: string;
}

interface Specialty {
  id: number;
  label: string;
  url: string;
  order: number;
  locale: string;
}

interface NavLink {
  id: number;
  label: string;
  url: string;
  submenu?: Specialty[] | any[];
}

 
export default function Navbar() {
  const [logo, setLogo] = useState<LogoData | null>(null);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [navLinks, setNavLinks] = useState<NavLink[]>([]);
  const [locateUs, setLocateUs] = useState<NavLink | null>(null);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [loading, setLoading] = useState(true);

   const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  const { language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const pathname = usePathname();

  const isDark = theme === "dark";
 

  useEffect(() => {
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

 

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setMobileMenuOpen(false);
      setActiveDropdown(null);
      setLangOpen(false);

      try {
        const res = await fetch(`/api/navbar?locale=${language}`, {
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`Navbar API error: ${res.status}`);

        const json = await res.json();
        if (cancelled) return;

        if (json.logo) {
          const imageUrl = json.logo.logo?.[0]?.url
            ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${json.logo.logo[0].url}`
            : null;

          setLogo({
            namelogo: json.logo.namelogo || "",
            Reference: json.logo.Reference || "",
            url: imageUrl,
          });
        } else {
          setLogo(null);
        }

        setSpecialties(Array.isArray(json.specialties) ? json.specialties : []);
        setNavLinks(Array.isArray(json.navLinks) ? json.navLinks : []);
        setLocateUs(json.locateUs || null);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching navbar data:", err);
          setLogo(null);
          setSpecialties([]);
          setNavLinks([]);
          setLocateUs(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [language]);

  if (loading) return null;
 

  const toggleMobileMenu = () => setMobileMenuOpen((p) => !p);

  const toggleDropdown = (id: number) =>
    setActiveDropdown((prev) => (prev === id ? null : id));

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setLangOpen(false);
  };

 

  const isActive = (url: string | undefined) => {
    if (!url) return false;
    if (url === "/") return pathname === "/";
    return pathname?.startsWith(url);
  };

 

  const ACTIVE_COLOR = "#3B82F6";
  const INACTIVE_COLOR = isDark ? "#F3F4F6" : "#374151";
  const HOVER_COLOR = isDark ? "#60A5FA" : "#2563EB";

  const NAVBAR_BG = isDark ? "#0A1929" : "#FFFFFF";
  const BORDER_CLASS = isDark ? "border-blue-900/30" : "border-gray-200";

  const DROPDOWN_BG = isDark ? "#0A1929" : "#FFFFFF";
  const DROPDOWN_BORDER = isDark ? "border-blue-900/50" : "border-gray-200";
  const HOVER_ROW = isDark ? "hover:bg-white/10" : "hover:bg-gray-100";

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 w-full z-50 border-b ${BORDER_CLASS} transition-colors duration-300`}
      style={{
        height: "64px",
        backgroundColor: NAVBAR_BG,
        fontFamily:
          'Arial, "Helvetica Neue", Helvetica, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-full relative">
        <div className="flex justify-center items-center h-full gap-6 lg:gap-10">
      
          <Link
            href="/"
            className="flex items-center gap-3 shrink-0 group"
            onClick={closeAllMenus}
          >
            {logo?.url && (
              <img
                src={logo.url}
                alt={logo.namelogo}
                className="w-8 h-8 object-contain transition-transform duration-300 group-hover:scale-110"
              />
            )}
            <div className="flex flex-col text-left">
              {logo?.namelogo && (
                <h1
                  className={`
                    font-semibold text-[14px] tracking-tight whitespace-nowrap
                    transition-colors duration-300 group-hover:text-blue-500
                    ${isDark ? "text-white" : "text-gray-900"}
                  `}
                >
                  {logo.namelogo}
                </h1>
              )}
              <span
                className={`
                  text-[9px] font-medium uppercase tracking-wider
                  transition-colors duration-300
                  ${isDark ? "text-gray-400" : "text-gray-500"}
                `}
              >
                {logo?.Reference || "UNE REFERENCE NATIONALE"}
              </span>
            </div>
          </Link>

 
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => {
              const active = isActive(link.url);
              const hasSub = link.submenu && link.submenu.length > 0;
              const isOpen = activeDropdown === link.id;

              return (
                <div
                  key={link.id}
                  className="relative"
                  onMouseEnter={() => {
                    setHoveredLink(link.url);
                    if (hasSub) setActiveDropdown(link.id);
                  }}
                  onMouseLeave={() => {
                    setHoveredLink(null);
                    if (hasSub) setActiveDropdown(null);
                  }}
                >
                  {hasSub ? (
                    <>
                      <button
                        className="
                          relative flex items-center gap-1
                          text-[13px] py-2
                          transition-colors duration-300
                        "
                        style={{
                          color:
                            active || isOpen
                              ? ACTIVE_COLOR
                              : hoveredLink === link.url
                              ? HOVER_COLOR
                              : INACTIVE_COLOR,
                        }}
                      >
                        {link.label}
                        <ChevronDown
                          className={`w-3 h-3 transition-transform duration-300 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          strokeWidth={2}
                        />

                        <span
                          className={`
                            absolute left-0 right-0 -bottom-0.5 h-[2px] rounded-full
                            transition-all duration-300 ease-out
                            ${
                              active || isOpen || hoveredLink === link.url
                                ? "opacity-100 scale-x-100"
                                : "opacity-0 scale-x-0"
                            }
                          `}
                          style={{
                            backgroundColor:
                              active || isOpen
                                ? ACTIVE_COLOR
                                : HOVER_COLOR,
                          }}
                        />
                      </button>

                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.15 }}
                            className={`
                              absolute top-full start-0 mt-3 w-56
                              rounded-md border ${DROPDOWN_BORDER}
                              shadow-2xl z-50 overflow-hidden
                            `}
                            style={{ backgroundColor: DROPDOWN_BG }}
                          >
                            <div className="py-1">
                              {link.submenu!.map((subItem: any) => {
                                const subActive = isActive(subItem.url);
                                return (
                                  <Link
                                    key={subItem.id}
                                    href={subItem.url}
                                    onClick={closeAllMenus}
                                    className={`
                                      block px-4 py-2.5 text-[12.5px]
                                      transition-all duration-200
                                      hover:ps-5
                                      ${HOVER_ROW}
                                    `}
                                    style={{
                                      color: subActive
                                        ? ACTIVE_COLOR
                                        : INACTIVE_COLOR,
                                    }}
                                  >
                                    {subItem.label}
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      href={link.url}
                      className="
                        relative block text-[13px] py-2
                        transition-colors duration-300
                      "
                      style={{
                        color: active
                          ? ACTIVE_COLOR
                          : hoveredLink === link.url
                          ? HOVER_COLOR
                          : INACTIVE_COLOR,
                      }}
                    >
                      {link.label}

                      <span
                        className={`
                          absolute left-0 right-0 -bottom-0.5 h-[2px] rounded-full
                          transition-all duration-300 ease-out
                          ${
                            active || hoveredLink === link.url
                              ? "opacity-100 scale-x-100"
                              : "opacity-0 scale-x-0"
                          }
                        `}
                        style={{
                          backgroundColor: active
                            ? ACTIVE_COLOR
                            : HOVER_COLOR,
                        }}
                      />
                    </Link>
                  )}
                </div>
              );
            })}

       
            {/*locateUs && (
              <Link
                href={locateUs.url}
                className="relative block text-[13px] py-2 transition-colors duration-300"
                onMouseEnter={() => setHoveredLink(locateUs.url)}
                onMouseLeave={() => setHoveredLink(null)}
                style={{
                  color: isActive(locateUs.url)
                    ? ACTIVE_COLOR
                    : hoveredLink === locateUs.url
                    ? HOVER_COLOR
                    : INACTIVE_COLOR,
                }}
              >
                {locateUs.label}
                <span
                  className={`
                    absolute left-0 right-0 -bottom-0.5 h-[2px] rounded-full
                    transition-all duration-300 ease-out
                    ${
                      isActive(locateUs.url) || hoveredLink === locateUs.url
                        ? "opacity-100 scale-x-100"
                        : "opacity-0 scale-x-0"
                    }
                  `}
                  style={{
                    backgroundColor: isActive(locateUs.url)
                      ? ACTIVE_COLOR
                      : HOVER_COLOR,
                  }}
                />
              </Link>
            )*/}

      
      
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                onMouseEnter={() => setHoveredLink("lang")}
                onMouseLeave={() => setHoveredLink(null)}
                className="
                  relative flex items-center gap-1
                  text-[11px] uppercase tracking-wider py-2
                  transition-colors duration-300
                "
                style={{
                  color:
                    hoveredLink === "lang" ? HOVER_COLOR : INACTIVE_COLOR,
                }}
              >
                {language}
                <ChevronDown
                  className={`w-3 h-3 transition-transform duration-300 ${
                    langOpen ? "rotate-180" : ""
                  }`}
                  strokeWidth={2}
                />
                <span
                  className={`
                    absolute left-0 right-0 -bottom-0.5 h-[2px] rounded-full
                    transition-all duration-300 ease-out
                    ${
                      hoveredLink === "lang"
                        ? "opacity-100 scale-x-100"
                        : "opacity-0 scale-x-0"
                    }
                  `}
                  style={{ backgroundColor: HOVER_COLOR }}
                />
              </button>

              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.15 }}
                    className={`
                      absolute top-full end-0 mt-3 w-20
                      rounded-md border ${DROPDOWN_BORDER}
                      shadow-2xl z-50 overflow-hidden
                    `}
                    style={{ backgroundColor: DROPDOWN_BG }}
                  >
                    {["en", "fr", "ar"].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          setLanguage(lang);
                          setLangOpen(false);
                        }}
                        className={`
                          block w-full text-start px-3 py-2
                          text-[11px] uppercase tracking-wider
                          transition-all duration-200
                          hover:ps-4
                          ${HOVER_ROW}
                        `}
                        style={{
                          color:
                            language === lang ? ACTIVE_COLOR : INACTIVE_COLOR,
                        }}
                      >
                        {lang}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

        
            <button
              onClick={toggleTheme}
              onMouseEnter={() => setHoveredLink("theme")}
              onMouseLeave={() => setHoveredLink(null)}
              aria-label="Toggle theme"
              className="
                relative flex items-center justify-center
                w-9 h-9 rounded-full
                transition-all duration-300
                hover:scale-110 active:scale-95
              "
              style={{
                color:
                  hoveredLink === "theme" ? HOVER_COLOR : INACTIVE_COLOR,
              }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {isDark ? (
                  <motion.span
                    key="sun"
                    initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: 90, opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Sun className="w-4 h-4" strokeWidth={2} />
                  </motion.span>
                ) : (
                  <motion.span
                    key="moon"
                    initial={{ rotate: 90, opacity: 0, scale: 0.5 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: -90, opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Moon className="w-4 h-4" strokeWidth={2} />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

      














      
        <div className="absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 lg:hidden flex items-center gap-1">
          
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`
              flex items-center justify-center w-10 h-10
              transition-transform duration-300 active:scale-90
              ${isDark ? "text-white" : "text-gray-900"}
            `}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isDark ? (
                <motion.span
                  key="sun-mobile"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <Sun className="w-4 h-4" strokeWidth={2} />
                </motion.span>
              ) : (
                <motion.span
                  key="moon-mobile"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <Moon className="w-4 h-4" strokeWidth={2} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <button
            onClick={toggleMobileMenu}
            aria-label="Menu"
            className={`
              flex items-center justify-center w-10 h-10
              transition-transform duration-300 active:scale-90
              ${isDark ? "text-white" : "text-gray-900"}
            `}
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5" strokeWidth={1.8} />
            ) : (
              <Menu className="w-5 h-5" strokeWidth={1.8} />
            )}
          </button>
        </div>

    
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className={`
                lg:hidden absolute left-0 right-0 top-full mt-2 mx-4 rounded-md
                border ${DROPDOWN_BORDER}
                shadow-2xl overflow-hidden z-50
              `}
              style={{ backgroundColor: DROPDOWN_BG }}
            >
              <div className="p-2">
                {navLinks.map((link) => {
                  const active = isActive(link.url);
                  return (
                    <div key={link.id}>
                      {link.submenu && link.submenu.length > 0 ? (
                        <div>
                          <button
                            onClick={() => toggleDropdown(link.id)}
                            className={`
                              flex items-center justify-between w-full
                              px-3 py-3 text-[13px] rounded
                              transition-all duration-200
                              ${HOVER_ROW}
                            `}
                            style={{
                              color:
                                active || activeDropdown === link.id
                                  ? ACTIVE_COLOR
                                  : INACTIVE_COLOR,
                            }}
                          >
                            {link.label}
                            <ChevronDown
                              className={`w-3.5 h-3.5 transition-transform duration-300 ${
                                activeDropdown === link.id ? "rotate-180" : ""
                              }`}
                              strokeWidth={2}
                            />
                          </button>

                          <AnimatePresence>
                            {activeDropdown === link.id && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="ps-4 overflow-hidden"
                              >
                                {link.submenu.map((subItem: any) => (
                                  <Link
                                    key={subItem.id}
                                    href={subItem.url}
                                    onClick={closeAllMenus}
                                    className={`
                                      block px-3 py-2.5 text-[12.5px] rounded
                                      transition-all duration-200
                                      hover:ps-4
                                      ${HOVER_ROW}
                                    `}
                                    style={{
                                      color: isActive(subItem.url)
                                        ? ACTIVE_COLOR
                                        : INACTIVE_COLOR,
                                    }}
                                  >
                                    {subItem.label}
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          href={link.url}
                          onClick={closeAllMenus}
                          className={`
                            block px-3 py-3 text-[13px] rounded
                            transition-all duration-200
                            hover:ps-4
                            ${HOVER_ROW}
                          `}
                          style={{
                            color: active ? ACTIVE_COLOR : INACTIVE_COLOR,
                          }}
                        >
                          {link.label}
                        </Link>
                      )}
                    </div>
                  );
                })}

                {locateUs && (
                  <Link
                    href={locateUs.url}
                    onClick={closeAllMenus}
                    className={`
                      block px-3 py-3 text-[13px] rounded
                      transition-all duration-200
                      hover:ps-4
                      ${HOVER_ROW}
                    `}
                    style={{
                      color: isActive(locateUs.url)
                        ? ACTIVE_COLOR
                        : INACTIVE_COLOR,
                    }}
                  >
                    {locateUs.label}
                  </Link>
                )}

                <Link
                  href="/shop"
                  onClick={closeAllMenus}
                  className={`
                    block px-3 py-3 text-[13px] rounded
                    transition-all duration-200
                    hover:ps-4
                    ${HOVER_ROW}
                  `}
                  style={{
                    color: isActive("/shop") ? ACTIVE_COLOR : INACTIVE_COLOR,
                  }}
                >
                  Shop Now
                </Link>

                <div
                  className={`mt-2 pt-3 border-t px-3 ${
                    isDark ? "border-white/10" : "border-gray-200"
                  }`}
                >
                  <div className="flex gap-4 pb-2">
                    {["en", "fr", "ar"].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          setLanguage(lang);
                          closeAllMenus();
                        }}
                        className="text-[11px] uppercase tracking-wider transition-colors duration-200 hover:text-blue-400"
                        style={{
                          color:
                            language === lang ? ACTIVE_COLOR : INACTIVE_COLOR,
                        }}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}

